import React, { useState } from 'react';
import { ChevronDown, Trash2, Loader2, ChevronsDown, ChevronsUp } from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog';
import { STATUS_META, STATUS_ORDER } from './taskMeta';

/**
 * Act on everything the filters are currently showing.
 *
 * Only rendered from the control bar's active-filter row, which means it can
 * only ever appear when something is narrowing the board. That is deliberate:
 * "mark all done" next to an unfiltered list of 53 tasks is a footgun, whereas
 * "mark these 4 overdue high-priority ones done" is the reason you filtered.
 *
 * The count is in every label for the same reason — you should never be able to
 * trigger this without seeing how much it touches.
 */
export default function BulkActions({ count, mode = 'active', onSetStatus, onDelete, onDemote, onPromote }) {
    const inBacklog = mode === 'backlog';
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [busy, setBusy] = useState(false);

    if (!count) return null;

    const run = async (fn) => {
        setBusy(true);
        try {
            await fn();
        } finally {
            setBusy(false);
            setConfirmingDelete(false);
        }
    };

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        disabled={busy}
                        className="inline-flex items-center gap-1 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground transition-colors hover:border-white/20 hover:text-foreground disabled:opacity-50"
                    >
                        {busy ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : null}
                        {count} selected
                        <ChevronDown className="h-2.5 w-2.5" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                    {/* In the backlog the three status moves are noise — you are
                        not deciding whether parked work is in progress. The one
                        move that matters there is back onto the board. */}
                    {inBacklog ? (
                        <DropdownMenuItem
                            onSelect={() => run(onPromote)}
                            className="gap-2 text-xs"
                        >
                            <ChevronsUp className="h-3.5 w-3.5" />
                            Promote {count} to board
                        </DropdownMenuItem>
                    ) : (
                        <>
                            {STATUS_ORDER.map((status) => {
                                const meta = STATUS_META[status];
                                const Icon = meta.icon;
                                return (
                                    <DropdownMenuItem
                                        key={status}
                                        onSelect={() => run(() => onSetStatus(status))}
                                        className="gap-2 text-xs"
                                    >
                                        <Icon className="h-3.5 w-3.5" />
                                        Move {count} to {meta.label}
                                    </DropdownMenuItem>
                                );
                            })}
                            <DropdownMenuSeparator />
                            {/* The reason this component is worth the trouble:
                                filter to P:Low + I:Low, then clear the board of
                                all of it in one move. No confirm — unlike delete
                                below, it is one click to undo. */}
                            <DropdownMenuItem
                                onSelect={() => run(onDemote)}
                                className="gap-2 text-xs"
                            >
                                <ChevronsDown className="h-3.5 w-3.5" />
                                Demote {count} to backlog
                            </DropdownMenuItem>
                        </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        // Radix closes the menu on select, which would unmount the
                        // dialog with it — so defer opening until the close settles.
                        onSelect={(e) => {
                            e.preventDefault();
                            setConfirmingDelete(true);
                        }}
                        className="gap-2 text-xs text-red-400 focus:bg-red-500/10 focus:text-red-400"
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete {count}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <AlertDialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete {count} task{count === 1 ? '' : 's'}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This deletes every task currently matching your filters. It can't be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            disabled={busy}
                            onClick={(e) => {
                                e.preventDefault();
                                run(onDelete);
                            }}
                            className="bg-red-500 text-white hover:bg-red-600"
                        >
                            {busy ? 'Deleting…' : `Delete ${count}`}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
